import React, { useEffect, useRef } from "react";
import { Row, Col, Button } from "antd";
import { gsap } from "gsap";
import { SendOutlined } from "@ant-design/icons";
import { SiHey } from "react-icons/si";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import backgroundbit from "../assets/background-component.png";
import tshirtImg from "../assets/right_side.png";

const Banner = () => {
  const bannerRef = useRef(null);
  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Only run animations on desktop
      if (window.innerWidth >= 768) {
        // ✅ Hero text animation
        gsap.from(".text-block", {
          scrollTrigger: {
            trigger: ".text-block",
            start: "top 85%",
            end: "top 40%",
            toggleActions: "play none none reverse",
          },
          y: 50,
          opacity: 0,
          duration: 1.5,
          ease: "power3.out",
          stagger: 0.2,
        });

        // ✅ T-shirt image animation
        gsap.from(".image-block img", {
          scrollTrigger: {
            trigger: ".image-block",
            start: "top 90%",
            end: "top 40%",
            toggleActions: "play none none reverse",
          },
          x: 120,
          opacity: 0,
          duration: 1.5,
          ease: "power3.out",
        });

        // ✅ Animated heading scaling
        gsap.from(".animated-text", {
          scrollTrigger: {
            trigger: ".animated-text",
            start: "top 85%",
            end: "top 30%",
            scrub: true,
          },
          scale: 0.8,
          opacity: 0.5,
          ease: "power2.out",
        });

        // ✅ Rotating word bounce
        gsap.fromTo(
          ".rotating-word",
          { rotation: -75, y: -80, opacity: 0 },
          {
            scrollTrigger: {
              trigger: ".animated-text",
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
            rotation: 0,
            y: 0,
            opacity: 1,
            ease: "bounce.out",
            duration: 1.8,
          }
        );

        // ✅ Social links
        gsap.from(".social-links li", {
          scrollTrigger: {
            trigger: ".social-links",
            start: "top 95%",
          },
          y: 40,
          opacity: 0,
          duration: 1,
          stagger: 0.3,
          ease: "back.out(1.7)",
        });

        // ✅ Background decoration
        gsap.from(".animate-slideDown", {
          scrollTrigger: {
            trigger: ".banner-short",
            start: "top 100%",
            toggleActions: "play none none reverse",
          },
          x: -100,
          opacity: 0,
          duration: 1.5,
          ease: "power2.out",
        });
      }
    }, bannerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={bannerRef}
      className="banner-short overflow-hidden relative min-h-screen bg-white py-8 md:py-16"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <Row gutter={[16, 32]} align="middle">
          {/* LEFT SECTION */}
          <Col xs={24} md={12} lg={12}>
            <div className="text-block">
              <p className="text-[16px] md:text-[20px] lg:text-[23px] font-medium text-black mb-3 md:mb-4 font-custom image-block">
                <span className="text-[#6cc5bf] text-2xl md:text-3xl lg:text-4xl">_</span> Design, Create,
                And Wear Your Story
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-black font-custom animated-text image-block leading-tight md:leading-snug lg:leading-[1.3]">
                Bring Your Ideas{" "}
                <span className="text-[#6cc5bf] rotating-word inline-block pr-2 md:pr-3">
                  To Life{" "}
                </span>
                On Custom <br className="hidden sm:block" />
                <span>T-Shirts</span>
              </h1>
              <p className="font-outfit w-full md:w-[90%] lg:w-[80%] text-gray-700 mt-3 md:mt-4 text-base md:text-lg font-light image-block">
                Your T-Shirt Is More Than Just Fabric — It's A Canvas For Your
                Ideas, Your Brand, And Your Story. At Initial Global Trade, We
                Make It Easy To Create Custom T-Shirts That Stand Out With Bold
                Colors, Durable Prints, And Endless Design Possibilities.
              </p>
              <Button
                className="relative font-custom text-base md:text-lg font-semibold
                  overflow-hidden border-2 border-teal-400 rounded-full
                  px-4 md:px-6 h-[40px] md:h-[45px] transition-all duration-300 ease-in-out
                  text-teal-600 bg-white hover:!bg-[#6cc5bf] hover:!border-[#000]
                  hover:!text-white shadow-[4px_4px_0_0_#14b8a6] md:shadow-[6px_6px_0_0_#14b8a6]
                  hover:shadow-[0px_0px_0_0_#14b8a6]
                  active:shadow-[0_2px_0_0_#14b8a6]
                  active:translate-y-[4px] group mt-6 md:mt-8"
                type="default"
                icon={<SendOutlined />}
              >
                <span className="hidden sm:inline">Let's Talk — Send A Message</span>
                <span className="sm:hidden">Let's Talk</span>
                <SiHey className="ml-2" />
              </Button>
            </div>
          </Col>

          {/* RIGHT SECTION */}
          <Col xs={24} md={12} lg={12}>
            <div className="image-block relative flex justify-center items-center mt-8 md:mt-0">
              <img
                src={tshirtImg}
                alt="Custom T-shirt"
                className="w-full max-w-[400px] md:max-w-full h-auto"
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* Decorative background - Hidden on mobile */}
      <img
        src={backgroundbit}
        alt="decor"
        className="hidden md:block absolute bottom-20 left-0 w-32 lg:w-40 animate-slideDown"
      />

      {/* Rotated Email - Hidden on mobile and tablet */}
      <div className="main-gmail social-links hidden xl:block">
        <a
          href="mailto:initialglobaltrade@gmail.com"
          className="font-outfit text-base lg:text-lg tracking-wider 
        absolute top-[140px] -right-14 rotate-[-90deg]
        bg-[#6cc5bf] text-white px-3 py-1 rounded-md shadow-md hover:bg-[#5ab4ae] duration-300"
        >
          initialglobaltrade@gmail.com
        </a>
      </div>

      {/* Social Links - Hidden on mobile and tablet */}
      <ul
        className="social-links font-outfit text-base lg:text-lg absolute top-[550px] -right-14 
        rotate-[-90deg] hidden xl:flex items-center justify-center gap-6 lg:gap-8"
      >
        <li>
          <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300">
            Instagram
          </a>
        </li>
        <li>
          <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300">
            Facebook
          </a>
        </li>
        <li>
          <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300">
            Twitter
          </a>
        </li>
      </ul>

      {/* Mobile Social Links - Bottom of page */}
      <div className="xl:hidden flex justify-center items-center gap-6 mt-8 pb-8">
        <a 
          href="mailto:initialglobaltrade@gmail.com"
          className="text-[#6cc5bf] hover:text-[#5ab4ae] font-outfit text-sm md:text-base transition-colors"
        >
          Email Us
        </a>
        <span className="text-gray-300">|</span>
        <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300 text-sm md:text-base">
          Instagram
        </a>
        <span className="text-gray-300">|</span>
        <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300 text-sm md:text-base">
          Facebook
        </a>
        <span className="text-gray-300">|</span>
        <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300 text-sm md:text-base">
          Twitter
        </a>
      </div>
    </div>
  );
};

export default Banner;